import React, { useRef, useState } from 'react';
import { Container, Button, useMantineColorScheme, Text, Group, Loader } from '@mantine/core';
import ModalComponent from '../modal';
import { fetchReceiptData } from '../../../libraries/apm';
import { useSelector } from 'react-redux';
import { AppState } from '../../client-redux/reducers';
import { Language } from '../../languages/languageHandler';
import { useStatusOperDialog } from '../../context/status-oper-dialog';
import { StatusType } from '../../../libraries/zps';
import { IconReceipt, IconPrinter } from '@tabler/icons-react';
import './styles.scss';

interface ShowReceiptProps {
  a001: number;
  movType: string;
  a031: number;
  a032: number;
  a029: number;
  dateTime: string;
}

const APMReceipt: React.FC<ShowReceiptProps> = ({ a001, movType, a031, a032, a029, dateTime }) => {
  const controllerRef = useRef<AbortController | null>(null);
  const lang = useSelector<AppState, Language>(state => state.lang.lang);

  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [receiptInfo, setReceiptInfo] = useState<string>('');
  const { colorScheme } = useMantineColorScheme();

  const [hasError, setHasError] = useState(false);

  const { handleApiError, showStatusOperDialog } = useStatusOperDialog();

  const openModal = async () => {
    if (showModal) return;

    setLoading(true);
    setReceiptInfo('');
    setHasError(false);

    controllerRef.current = new AbortController();

    try {
      const response = await fetchReceiptData(a001, movType, a031, a032, a029, dateTime, controllerRef.current);

      const status = response?.data?.statusOper;

      if (status?.code !== 0) {
        setHasError(true);
        showStatusOperDialog({
          type: StatusType.ERROR,
          message: lang.PERIODS_RECEIPT_ERROR,
          statusOper: status
        });

        return;
      }

      setShowModal(true);

      if (response?.data?.receipt) {
        const decodedReceipt = decodeURIComponent(escape(atob(response.data.receipt)));
        const formattedReceipt = decodedReceipt.replace(/ /g, '&nbsp;').replace(/\n/g, '<br />');

        setReceiptInfo(formattedReceipt);
      } else {
        setReceiptInfo('');
      }
    } catch (error) {
      setHasError(true);

      handleApiError(error, lang.PERIODS_RECEIPT_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    controllerRef.current?.abort();
  };

  const handlePrint = () => {
    const iframe = document.createElement('iframe');

    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';

    document.body.appendChild(iframe);

    const doc = iframe.contentWindow?.document;

    if (!doc) return;

    doc.open();
    doc.write(`
    <html>
      <body style="font-family:courier;font-size:12px;">
        ${receiptInfo}
        <script>
          window.onload = function () {
            window.print();
            setTimeout(() => window.parent.document.body.removeChild(window.frameElement), 300);
          };
        </script>
      </body>
    </html>
  `);
    doc.close();
  };

  return (
    <>
      <IconReceipt stroke={2} onClick={openModal} />

      <ModalComponent
        show={showModal}
        handleClick={closeModal}
        title={lang.GLOBAL_RECEIPT}
        content={
          <Container bg={colorScheme === 'dark' ? '#1b1b1b' : 'white'} classNames={{ root: 'APMReceipt' }}>
            <Group className="apmReceiptDetails">
              {loading ? (
                <Loader color="#016273" />
              ) : hasError ? null : !receiptInfo ? (
                <Text style={{ textAlign: 'center' }}>{lang.GLOBAL_NO_RESULTS}</Text>
              ) : (
                <Text dangerouslySetInnerHTML={{ __html: receiptInfo }} />
              )}
            </Group>

            <Group className="button">
              <Button color="#016273" radius="md" disabled={!receiptInfo} onClick={handlePrint}>
                <IconPrinter stroke={2} />
              </Button>
            </Group>
          </Container>
        }
      />
    </>
  );
};

export default APMReceipt;
